// src/pages/markets/board/board/ColumnFilters.tsx
//
// Sections below the category and tag blocks are rendered from
// schema.filter_spec[column] through a type → renderer registry. Every column
// declares its own fields, so min capital appears on the conviction columns and
// nowhere else without this file knowing which column is which.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    TextField,
    Button,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    FILTER_FIELD_BY_WIRE,
    type BoardFilters,
    type BoardSchema,
    type FilterFieldSpec,
    type MetricMode,
    type TagMatch,
} from '../api/boardTypes';
import {
    filterWireValues,
    fieldLabel,
    fieldUnit,
    filterFields,
    isFieldEnabled,
    modeThresholdPatch,
} from '../api/useSchema';
import { useMutedTopics } from '../state/useMutedTopics';
import { SectionLabel } from '../../../../components/common/page/SectionLabel';
import {
    toolbarButtonSx,
    toolbarToggleSx,
} from '../../../../components/common/page/toolbarStyles';

export interface ColumnFiltersProps {
    schema: BoardSchema;
    column: string;
    effective: BoardFilters;
    onChange: (patch: Partial<BoardFilters>) => void;
    onReset: () => void;
    onShare: () => string;
    // Finger-sized controls and text of at least 12 px, for the phone sheet.
    touch?: boolean;
}

type TriState = 'off' | 'include' | 'exclude';

const SECTION_TITLES: Record<string, string> = {
    move: 'Move',
    volume: 'Volume',
    resolved: 'Resolved markets',
    thresholds: 'Thresholds',
    age: 'Score age',
};

const SECTION_ORDER = ['move', 'volume', 'resolved', 'thresholds', 'age'];

const SECTION_NOTES: Record<string, string> = {
    move: 'Drift is the net move over its window. Jump is the sharpest move of its length inside that same window.',
    resolved: 'A market counts as resolved once it holds past 99% for five minutes with no contrary print.',
    age: 'Scores are written on change, so an untouched score is stable, not stale. This bounds the scan, it does not filter for relevance.',
};

// The scope control lives in the header, so its spec is rendered there and
// skipped here.
const HEADER_SECTIONS = new Set(['header']);

const FIELD_LABELS: Record<string, string> = {
    scope: 'Scope',
    event_agg: 'Across event',
    metric_mode: 'Unit',
    drift_window: 'Drift over',
    min_drift: 'Min drift',
    jump_window: 'Jump within',
    min_jump: 'Min jump',
    min_volume_usd: 'Min volume',
    direction: 'Direction',
    resolved_mode: 'Resolved',
    min_capital: 'Min capital',
    min_traders: 'Min traders',
    max_age_days: 'Max age',
};

const TRISTATE_LABELS: Record<string, string> = {
    exclude: 'Hide',
    include: 'Show',
    only: 'Only',
};

function humanize(key: string): string {
    const s = key.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function labelFor(spec: FilterFieldSpec): string {
    return FIELD_LABELS[spec.key] ?? humanize(spec.key);
}

function stateOf(value: string, include: string[] | null, exclude: string[] | null): TriState {
    if (include?.includes(value)) return 'include';
    if (exclude?.includes(value)) return 'exclude';
    return 'off';
}

// Off → Include → Exclude → Off. Both lists are returned every time so the
// caller patches them as one atomic change.
function cycle(
    value: string,
    include: string[] | null,
    exclude: string[] | null,
): { include: string[]; exclude: string[] } {
    const inc = include ?? [];
    const exc = exclude ?? [];
    switch (stateOf(value, include, exclude)) {
        case 'off':
            return { include: [...inc, value], exclude: exc };
        case 'include':
            return { include: inc.filter(x => x !== value), exclude: [...exc, value] };
        default:
            return { include: inc, exclude: exc.filter(x => x !== value) };
    }
}

// The phone sheet's sizes: 44 px to hit, and nothing under 12 px to read.
const TOUCH_TARGET = 44;
const touchChipSx = {
    height: TOUCH_TARGET,
    minWidth: TOUCH_TARGET,
    fontSize: '14px',
    borderRadius: '8px',
    '& .MuiChip-label': { px: 1.5 },
} as const;
const touchToggleSx = {
    ...toolbarToggleSx,
    height: 'auto',
    '& .MuiToggleButton-root': {
        ...toolbarToggleSx['& .MuiToggleButton-root'],
        height: TOUCH_TARGET,
        minWidth: TOUCH_TARGET,
        px: 1.5,
        fontSize: '14px',
    },
} as const;
const touchButtonSx = {
    height: TOUCH_TARGET,
    minWidth: TOUCH_TARGET,
    px: 2,
    fontSize: '14px',
} as const;

const MUTED_TITLE =
    'Hidden everywhere by your preferences. Change it in Settings → Hidden topics.';

function TriChipRow({
    options,
    include,
    exclude,
    muted,
    onCycle,
    touch,
}: {
    options: string[];
    include: string[] | null;
    exclude: string[] | null;
    /** Muted in the user's own preferences — shown, but not cyclable here. */
    muted?: readonly string[];
    onCycle: (value: string) => void;
    touch?: boolean;
}) {
    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: touch ? 1 : 0.5 }}>
            {options.map(opt => {
                // A muted option keeps its place in the row rather than
                // vanishing: a row that silently loses entries reads as a bug,
                // and the reader has no way back to the setting from a gap.
                if (muted?.includes(opt)) {
                    return (
                        <Tooltip key={opt} title={MUTED_TITLE}>
                            <Chip
                                label={`− ${opt}`}
                                size="small"
                                // Focusable so the tooltip is reachable without a
                                // mouse; it is the only place the lock is explained.
                                tabIndex={0}
                                aria-label={`${opt} — ${MUTED_TITLE}`}
                                sx={{
                                    height: 24,
                                    fontSize: '11px',
                                    borderRadius: '4px',
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    bgcolor: 'transparent',
                                    color: 'text.disabled',
                                    fontWeight: 400,
                                    textDecoration: 'line-through',
                                    cursor: 'help',
                                    ...(touch && touchChipSx),
                                }}
                            />
                        </Tooltip>
                    );
                }

                const state = stateOf(opt, include, exclude);
                const hue =
                    state === 'include'
                        ? theme.palette.success.main
                        : state === 'exclude'
                            ? theme.palette.error.main
                            : null;
                return (
                    <Chip
                        key={opt}
                        label={state === 'exclude' ? `− ${opt}` : opt}
                        size="small"
                        onClick={() => onCycle(opt)}
                        sx={{
                            height: 24,
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid',
                            borderColor: hue ? alpha(hue, 0.6) : 'transparent',
                            bgcolor: hue ? alpha(hue, 0.16) : 'action.hover',
                            color: hue ?? 'text.secondary',
                            fontWeight: hue ? 700 : 400,
                            textDecoration: state === 'exclude' ? 'line-through' : 'none',
                            '&:hover': {
                                bgcolor: hue ? alpha(hue, 0.26) : 'action.selected',
                            },
                            ...(touch && touchChipSx),
                        }}
                    />
                );
            })}
        </Box>
    );
}

function NumberField({
    label,
    value,
    unit,
    disabled,
    onCommit,
    touch,
}: {
    label: string;
    value: number | null;
    unit?: string;
    disabled?: boolean;
    onCommit: (v: number | null) => void;
    touch?: boolean;
}) {
    const [text, setText] = useState(value === null ? '' : String(value));

    useEffect(() => {
        setText(value === null ? '' : String(value));
    }, [value]);

    const commit = () => {
        const trimmed = text.trim();
        if (trimmed === '') return onCommit(null);
        const n = Number(trimmed);
        onCommit(Number.isFinite(n) ? n : null);
    };

    return (
        <TextField
            label={label}
            value={text}
            size="small"
            disabled={disabled}
            onChange={e => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === 'Enter') commit();
            }}
            InputProps={{
                // 16 px: iOS zooms into a smaller field, and a label that
                // shrinks to 0.75 of its size stays at 12.
                sx: { fontSize: touch ? '16px' : '12px' },
                endAdornment: unit ? (
                    <InputAdornment position="end">
                        <Typography sx={{ fontSize: touch ? '13px' : '11px', color: 'text.disabled' }}>
                            {unit}
                        </Typography>
                    </InputAdornment>
                ) : undefined,
            }}
            InputLabelProps={{ sx: { fontSize: touch ? '16px' : '12px' } }}
            sx={{
                width: touch ? 150 : 130,
                // Whatever the field's variant pads it to, 44 px at least.
                ...(touch && {
                    '& .MuiInputBase-root': { minHeight: TOUCH_TARGET },
                    '& .MuiInputBase-input': { boxSizing: 'border-box', minHeight: TOUCH_TARGET },
                }),
            }}
        />
    );
}

function EnumField({
    label,
    options,
    labels,
    value,
    disabled,
    onChange,
    touch,
}: {
    label: string;
    options: string[];
    labels: (opt: string) => string;
    value: string | undefined;
    disabled?: boolean;
    onChange: (v: string) => void;
    touch?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: touch ? 'wrap' : 'nowrap', gap: 1, opacity: disabled ? 0.45 : 1 }}>
            <Typography
                sx={{ fontSize: touch ? '13px' : '11px', color: 'text.secondary', width: 84, flexShrink: 0 }}
            >
                {label}
            </Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                disabled={disabled}
                value={value ?? null}
                onChange={(_, v: string | null) => {
                    if (v) onChange(v);
                }}
                sx={{
                    ...toolbarToggleSx,
                    height: 'auto',
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                        ...toolbarToggleSx['& .MuiToggleButton-root'],
                        height: toolbarToggleSx.height,
                        ...(touch && touchToggleSx['& .MuiToggleButton-root']),
                    },
                }}
            >
                {options.map(opt => (
                    <ToggleButton key={opt} value={opt}>
                        {labels(opt)}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}

// Same shape as the tag include/exclude control, so hide/show/only reads the
// way keep/drop already does elsewhere in this panel.
function TristateField({
    label,
    options,
    value,
    onChange,
    touch,
}: {
    label: string;
    options: string[];
    value: string | undefined;
    onChange: (v: string) => void;
    touch?: boolean;
}) {
    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: touch ? 'wrap' : 'nowrap', gap: 1 }}>
            <Typography
                sx={{ fontSize: touch ? '13px' : '11px', color: 'text.secondary', width: 84, flexShrink: 0 }}
            >
                {label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: touch ? 'wrap' : 'nowrap', gap: touch ? 1 : 0.5 }}>
                {options.map(opt => {
                    const on = value === opt;
                    const hue =
                        opt === 'exclude'
                            ? theme.palette.error.main
                            : opt === 'only'
                                ? theme.palette.warning.main
                                : theme.palette.success.main;
                    return (
                        <Chip
                            key={opt}
                            label={TRISTATE_LABELS[opt] ?? opt}
                            size="small"
                            onClick={() => onChange(opt)}
                            sx={{
                                height: 24,
                                fontSize: '11px',
                                borderRadius: '4px',
                                border: '1px solid',
                                borderColor: on ? alpha(hue, 0.6) : 'transparent',
                                bgcolor: on ? alpha(hue, 0.16) : 'action.hover',
                                color: on ? hue : 'text.secondary',
                                fontWeight: on ? 700 : 400,
                                '&:hover': { bgcolor: on ? alpha(hue, 0.26) : 'action.selected' },
                                ...(touch && touchChipSx),
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

function Note({ children, touch }: { children: React.ReactNode; touch?: boolean }) {
    return (
        <Typography sx={{ fontSize: touch ? '12px' : '11px', color: 'text.secondary', mt: 0.75 }}>
            {children}
        </Typography>
    );
}

export function ColumnFilters({
    schema,
    column,
    effective,
    onChange,
    onReset,
    onShare,
    touch,
}: ColumnFiltersProps) {
    const { effective: muted } = useMutedTopics();
    const labelSx = touch ? { fontSize: '12px' } : undefined;
    const [copied, setCopied] = useState(false);
    // A threshold the reader has typed into must survive a unit switch; one
    // they never touched is still expressing the seeded strictness and has to
    // be re-expressed in the new unit.
    const [touched, setTouched] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const fields = useMemo(() => filterFields(schema, column), [schema, column]);
    const values = useMemo(() => filterWireValues(effective), [effective]);

    const sections = useMemo(() => {
        const bySection = new Map<string, FilterFieldSpec[]>();
        for (const spec of fields) {
            const key = spec.section ?? 'other';
            if (HEADER_SECTIONS.has(key)) continue;
            const list = bySection.get(key);
            if (list) list.push(spec);
            else bySection.set(key, [spec]);
        }
        const known = SECTION_ORDER.filter(s => bySection.has(s));
        const rest = [...bySection.keys()].filter(s => !SECTION_ORDER.includes(s));
        return [...known, ...rest].map(name => ({ name, specs: bySection.get(name) ?? [] }));
    }, [fields]);

    const setField = useCallback(
        (spec: FilterFieldSpec, value: string | number | null) => {
            const field = FILTER_FIELD_BY_WIRE[spec.key];
            if (!field) return;
            setTouched(prev => {
                if (prev.has(spec.key)) return prev;
                const next = new Set(prev);
                next.add(spec.key);
                return next;
            });
            const patch = { [field]: value } as Partial<BoardFilters>;
            // Switching the unit relabels every threshold, so the ones still
            // sitting on a seeded value have to move with it.
            if (spec.key === 'metric_mode' && typeof value === 'string') {
                Object.assign(patch, modeThresholdPatch(schema, column, value as MetricMode, touched));
            }
            onChange(patch);
        },
        [onChange, schema, column, touched],
    );

    const handleShare = async () => {
        const url = onShare();
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            window.prompt('Copy this link', url);
        }
    };

    function renderField(spec: FilterFieldSpec): React.ReactNode {
        const field = FILTER_FIELD_BY_WIRE[spec.key];
        if (!field) return null;
        const enabled = isFieldEnabled(spec, values);
        const raw = effective[field];

        if (spec.type === 'enum') {
            return (
                <EnumField
                    key={spec.key}
                    label={labelFor(spec)}
                    options={spec.options ?? []}
                    labels={opt => fieldLabel(spec, opt)}
                    value={raw === undefined || raw === null ? undefined : String(raw)}
                    disabled={!enabled}
                    onChange={v => setField(spec, v)}
                    touch={touch}
                />
            );
        }

        if (spec.type === 'tristate') {
            return (
                <TristateField
                    key={spec.key}
                    label={labelFor(spec)}
                    options={spec.options ?? []}
                    value={raw === undefined || raw === null ? undefined : String(raw)}
                    onChange={v => setField(spec, v)}
                    touch={touch}
                />
            );
        }

        return (
            <NumberField
                key={spec.key}
                label={labelFor(spec)}
                value={typeof raw === 'number' ? raw : null}
                unit={fieldUnit(spec, values)}
                disabled={!enabled}
                onCommit={v => setField(spec, v)}
                touch={touch}
            />
        );
    }

    return (
        <Box sx={{ p: 2, width: touch ? '100%' : 360, boxSizing: touch ? 'border-box' : undefined, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
                <SectionLabel sx={labelSx}>Event category</SectionLabel>
                <TriChipRow
                    options={schema.event_categories}
                    include={effective.eventsInclude}
                    exclude={effective.eventsExclude}
                    muted={muted.categories}
                    onCycle={value => {
                        const next = cycle(value, effective.eventsInclude, effective.eventsExclude);
                        onChange({ eventsInclude: next.include, eventsExclude: next.exclude });
                    }}
                    touch={touch}
                />
                <Note touch={touch}>A market has one category, so there is no Any/All here.</Note>
            </Box>

            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.75,
                    }}
                >
                    <SectionLabel sx={{ mb: 0, ...labelSx }}>Tag</SectionLabel>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={effective.tagMatch}
                        onChange={(_, v: TagMatch | null) => {
                            if (v) onChange({ tagMatch: v });
                        }}
                        sx={touch ? touchToggleSx : toolbarToggleSx}
                    >
                        <ToggleButton value="any">Any</ToggleButton>
                        <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <TriChipRow
                    options={schema.polymarket_tags}
                    include={effective.tagsInclude}
                    exclude={effective.tagsExclude}
                    muted={muted.tags}
                    onCycle={value => {
                        const next = cycle(value, effective.tagsInclude, effective.tagsExclude);
                        onChange({ tagsInclude: next.include, tagsExclude: next.exclude });
                    }}
                    touch={touch}
                />
                <Note touch={touch}>
                    {touch ? 'Tap' : 'Click'} cycles off → keep → drop. Nothing on green means every tag passes.
                    Any/All applies to the green set only.
                </Note>
            </Box>

            {sections.map(({ name, specs }) => (
                <Box key={name}>
                    <SectionLabel sx={labelSx}>{SECTION_TITLES[name] ?? humanize(name)}</SectionLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {specs.map(renderField)}
                    </Box>
                    {SECTION_NOTES[name] && <Note touch={touch}>{SECTION_NOTES[name]}</Note>}
                </Box>
            ))}

            <Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={onReset}
                        sx={{ ...toolbarButtonSx, ...(touch && touchButtonSx), borderColor: 'divider' }}
                    >
                        Reset
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={handleShare}
                        sx={{ ...toolbarButtonSx, ...(touch && touchButtonSx), borderColor: 'divider' }}
                    >
                        {copied ? 'Copied' : 'Copy link'}
                    </Button>
                </Box>
                <Note touch={touch}>
                    Reset clears every filter and does not bring the preset tags back. Copy link
                    shares this column's filters.
                </Note>
            </Box>
        </Box>
    );
}